import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getBookmarks, createBookmark, updateBookmark, deleteBookmark } from './api';
import { useToast } from './Toast';
import ConfirmDialog from './ui/ConfirmDialog';
import PageHeader from './ui/PageHeader';
import EmptyState from './ui/EmptyState';
import Alert from './ui/Alert';
import Spinner from './ui/Spinner';

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return matches;
};

const truncateText = (text, maxLength = 80) => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return dateStr; }
};

const TagList = ({ tags, max = 4 }) => {
  const list = tags || [];
  if (list.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
      {list.slice(0, max).map((tag, i) => (
        <span key={i} className="badge badge-info" style={{ textTransform: 'none' }}>{tag}</span>
      ))}
      {list.length > max && <span className="muted small">+{list.length - max}</span>}
    </div>
  );
};

function Bookmarks() {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToast } = useToast();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingBookmark, setEditingBookmark] = useState(null);
  const [formData, setFormData] = useState({
    url: '', title: '', description: '', excerpt: '', tags: '', notes: '', domain_ids: '',
  });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const perPage = 20;

  const fetchBookmarks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = searchParams.get('q') || '';
      const page = parseInt(searchParams.get('page') || '1', 10);
      setCurrentPage(page);
      const data = await getBookmarks({ q, page, per_page: perPage });
      setBookmarks(data.bookmarks || []);
      setTotalResults(data.total || 0);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load bookmarks');
      setBookmarks([]);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchBookmarks();
  }, [fetchBookmarks]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = {};
    if (searchQuery.trim()) params.q = searchQuery.trim();
    params.page = '1';
    setSearchParams(params);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchParams({ page: '1' });
  };

  const handlePageChange = (newPage) => {
    const params = Object.fromEntries(searchParams.entries());
    params.page = String(newPage);
    setSearchParams(params);
  };

  const totalPages = Math.ceil(totalResults / perPage);

  // --- Modal helpers ---
  const openCreateModal = () => {
    setEditingBookmark(null);
    setFormData({ url: '', title: '', description: '', excerpt: '', tags: '', notes: '', domain_ids: '' });
    setShowModal(true);
  };

  const openEditModal = (bookmark) => {
    setEditingBookmark(bookmark);
    setFormData({
      url: bookmark.url || '',
      title: bookmark.title || '',
      description: bookmark.description || '',
      excerpt: bookmark.excerpt || '',
      tags: (bookmark.tags || []).join(', '),
      notes: bookmark.notes || '',
      domain_ids: (bookmark.domain_ids || []).join(', '),
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBookmark(null);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        url: formData.url.trim(),
        title: formData.title.trim(),
        description: formData.description.trim(),
        excerpt: formData.excerpt.trim(),
        notes: formData.notes.trim(),
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        domain_ids: formData.domain_ids.split(',').map(d => d.trim()).filter(Boolean),
      };
      if (editingBookmark) {
        await updateBookmark(editingBookmark.id, payload);
      } else {
        await createBookmark(payload);
      }
      closeModal();
      addToast(editingBookmark ? 'Bookmark updated' : 'Bookmark saved', 'success');
      // Small delay for Meilisearch indexing
      setTimeout(() => fetchBookmarks(), 500);
    } catch (err) {
      addToast(err.response?.data?.detail || 'Failed to save bookmark', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bookmarkId) => {
    try {
      await deleteBookmark(bookmarkId);
      setDeleteConfirm(null);
      addToast('Bookmark deleted', 'success');
      setTimeout(() => fetchBookmarks(), 500);
    } catch (err) {
      setDeleteConfirm(null);
      addToast(err.response?.data?.detail || 'Failed to delete bookmark', 'error');
    }
  };

  const activeQuery = searchParams.get('q');

  // --- Render ---
  return (
    <div className="page">
      <PageHeader
        title="Bookmarks"
        subtitle="Saved links, excerpts and notes for research"
        actions={
          <button type="button" className="btn btn-primary" onClick={openCreateModal}>
            + Add bookmark
          </button>
        }
      />

      <form onSubmit={handleSearch} className="toolbar">
        <input
          type="text"
          className="input grow"
          placeholder="Search bookmarks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <button type="submit" className="btn btn-primary">Search</button>
        {activeQuery && (
          <button type="button" className="btn btn-secondary" onClick={handleClearSearch}>
            Clear
          </button>
        )}
        <span className="muted small">
          {loading ? 'Loading...' : `${totalResults} bookmark${totalResults !== 1 ? 's' : ''}`}
          {activeQuery && ` matching "${activeQuery}"`}
        </span>
      </form>

      <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>

      {loading && bookmarks.length === 0 ? (
        <Spinner label="Loading bookmarks…" />
      ) : bookmarks.length === 0 ? (
        <EmptyState
          title={activeQuery ? 'No bookmarks match your search' : 'No bookmarks yet'}
          message={activeQuery ? 'Try a different search term.' : 'Save a link to start your research library.'}
          action={
            activeQuery ? (
              <button type="button" className="btn btn-secondary" onClick={handleClearSearch}>Clear search</button>
            ) : (
              <button type="button" className="btn btn-primary" onClick={openCreateModal}>+ Add bookmark</button>
            )
          }
        />
      ) : isMobile ? (
        /* Mobile cards */
        <div className="stack">
          {bookmarks.map((bm) => (
            <div key={bm.id} className="card">
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                <a href={bm.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                  {bm.title || bm.url}
                </a>
              </div>
              {bm.description && (
                <div className="muted" style={{ fontSize: '13px', marginBottom: '6px' }}>
                  {truncateText(bm.description, 120)}
                </div>
              )}
              <div className="muted small" style={{ marginBottom: '6px' }}>
                {bm.source_domain} &middot; {formatDate(bm.saved_at)}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <TagList tags={bm.tags} max={6} />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => openEditModal(bm)}>Edit</button>
                <button type="button" className="btn btn-ghost btn-sm btn-danger-text" onClick={() => setDeleteConfirm(bm.id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Desktop table */
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th style={{ width: '180px' }}>Source</th>
                <th style={{ width: '200px' }}>Tags</th>
                <th style={{ width: '110px' }}>Saved</th>
                <th className="actions" style={{ width: '140px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookmarks.map((bm) => (
                <tr key={bm.id}>
                  <td>
                    <a href={bm.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', fontWeight: 500 }}>
                      {truncateText(bm.title || bm.url, 60)}
                    </a>
                    {bm.description && (
                      <div className="muted small" style={{ marginTop: '2px' }}>
                        {truncateText(bm.description, 100)}
                      </div>
                    )}
                  </td>
                  <td className="muted">{bm.source_domain || '—'}</td>
                  <td><TagList tags={bm.tags} /></td>
                  <td className="muted small">{formatDate(bm.saved_at)}</td>
                  <td className="actions">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => openEditModal(bm)}>Edit</button>
                    <button type="button" className="btn btn-ghost btn-sm btn-danger-text" onClick={() => setDeleteConfirm(bm.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '20px' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            Previous
          </button>
          <span className="muted small">Page {currentPage} of {totalPages}</span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
          >
            Next
          </button>
        </div>
      )}

      <ConfirmDialog
        open={deleteConfirm !== null}
        title="Delete bookmark?"
        message="This will permanently remove the bookmark."
        confirmLabel="Delete"
        danger
        onConfirm={() => handleDelete(deleteConfirm)}
        onCancel={() => setDeleteConfirm(null)}
      />

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-backdrop" onClick={closeModal}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bookmark-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="bookmark-modal-title">{editingBookmark ? 'Edit bookmark' : 'Add bookmark'}</h3>
            </div>

            <div className="modal-body">
              <div className="field">
                <label htmlFor="bm-url">URL *</label>
                <input
                  id="bm-url"
                  className="input"
                  type="url" value={formData.url} onChange={(e) => handleFormChange('url', e.target.value)}
                  placeholder="https://example.com/article"
                />
              </div>
              <div className="field">
                <label htmlFor="bm-title">Title</label>
                <input
                  id="bm-title"
                  className="input"
                  value={formData.title} onChange={(e) => handleFormChange('title', e.target.value)}
                  placeholder="Article title"
                />
              </div>
              <div className="field">
                <label htmlFor="bm-description">Description</label>
                <textarea
                  id="bm-description"
                  className="textarea"
                  value={formData.description} onChange={(e) => handleFormChange('description', e.target.value)}
                  placeholder="Brief description"
                  rows={3}
                />
              </div>
              <div className="field">
                <label htmlFor="bm-excerpt">Excerpt</label>
                <textarea
                  id="bm-excerpt"
                  className="textarea"
                  value={formData.excerpt} onChange={(e) => handleFormChange('excerpt', e.target.value)}
                  placeholder="Key quote or excerpt"
                  rows={2}
                />
              </div>
              <div className="field">
                <label htmlFor="bm-tags">Tags</label>
                <input
                  id="bm-tags"
                  className="input"
                  value={formData.tags} onChange={(e) => handleFormChange('tags', e.target.value)}
                  placeholder="research, seo, marketing"
                />
                <span className="hint">Comma-separated.</span>
              </div>
              <div className="field">
                <label htmlFor="bm-domains">Related domains</label>
                <input
                  id="bm-domains"
                  className="input"
                  value={formData.domain_ids} onChange={(e) => handleFormChange('domain_ids', e.target.value)}
                  placeholder="frontanalytics_com, techliving_com"
                />
                <span className="hint">Comma-separated domain IDs.</span>
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="bm-notes">Notes</label>
                <textarea
                  id="bm-notes"
                  className="textarea"
                  value={formData.notes} onChange={(e) => handleFormChange('notes', e.target.value)}
                  placeholder="Personal notes about this bookmark"
                  rows={2}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving || !formData.url.trim()}
              >
                {saving ? 'Saving...' : editingBookmark ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Bookmarks;
